import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { apiRequest } from "../context/AuthContext";
import { Library, Clock, ChevronRight, Loader2, BookOpen } from "lucide-react";

const MODULE_COLORS = [
  { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200" },
  { bg: "bg-blue-100",   text: "text-blue-700",   border: "border-blue-200" },
  { bg: "bg-green-100",  text: "text-green-700",  border: "border-green-200" },
  { bg: "bg-purple-100", text: "text-purple-700", border: "border-purple-200" },
  { bg: "bg-amber-100",  text: "text-amber-700",  border: "border-amber-200" },
];

export default function LearnPage() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  useEffect(() => {
    apiRequest("get", "/learn/modules?type=recruiting")
      .then(r => setModules(r.data || []))
      .catch(() => setError("Could not load modules. Please try again."))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }}>
      {/* Header */}
      <div className="mb-8">
        <span className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">
          Recruiting Guide
        </span>
        <h1
          className="text-3xl font-bold text-slate-900 mt-1"
          style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}
        >
          UK Recruiting Learning Hub
        </h1>
        <p className="text-slate-500 mt-1.5 text-sm max-w-xl">
          Everything a UK player needs to understand the US college basketball
          recruiting process — from divisions and scholarships to eligibility and
          building your profile.
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
        </div>

      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-red-700 text-sm">
          {error}
        </div>

      ) : modules.length === 0 ? (
        /* Empty state */
        <div className="bg-white border border-slate-200 rounded-2xl p-16 text-center max-w-lg mx-auto">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
            <Library className="w-8 h-8 text-slate-400" />
          </div>
          <h3
            className="font-bold text-slate-700 text-xl mb-2"
            style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}
          >
            Modules Coming Soon
          </h3>
          <p className="text-slate-400 text-sm leading-relaxed">
            The recruiting guide is being prepared. Check back shortly — five
            in-depth modules covering everything from divisions to eligibility
            will be live here.
          </p>
        </div>

      ) : (
        /* Module grid */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="module-grid">
          {modules.map((mod, idx) => {
            const c = MODULE_COLORS[idx % MODULE_COLORS.length];
            return (
              <Link
                key={mod.slug}
                to={`/learn/${mod.slug}`}
                data-testid={`module-card-${mod.slug}`}
                className="bg-white border border-slate-200 rounded-2xl p-6 hover:border-orange-300 hover:shadow-md transition-all group flex flex-col gap-4"
              >
                {/* Number + read time */}
                <div className="flex items-start justify-between">
                  <div
                    className={`w-10 h-10 ${c.bg} ${c.border} border rounded-xl flex items-center justify-center flex-shrink-0`}
                  >
                    <span className={`text-sm font-black ${c.text}`}
                      style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                      {String(mod.order_index).padStart(2, "0")}
                    </span>
                  </div>
                  <span className="flex items-center gap-1 text-xs text-slate-400 font-medium">
                    <Clock className="w-3 h-3" />
                    {mod.estimated_read_minutes} min
                  </span>
                </div>

                {/* Title + description */}
                <div className="flex-1">
                  <h2
                    className="font-bold text-slate-900 text-base mb-2 group-hover:text-orange-600 transition-colors"
                    style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase", letterSpacing: "0.04em" }}
                  >
                    {mod.title}
                  </h2>
                  <p className="text-slate-500 text-sm leading-relaxed line-clamp-3">
                    {mod.description}
                  </p>
                </div>

                {/* CTA */}
                <div className="flex items-center gap-1 text-xs font-bold text-orange-500 group-hover:gap-2 transition-all">
                  <BookOpen className="w-3.5 h-3.5" />
                  <span>Read module</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}
